/**
 * LockBreakdownView — the lock-reservations dialog's pre-commit breakdown
 * (action-breakdown-remaining-dialogs task 4.2). Extracted out of
 * lock-reservations-dialog.tsx to keep that file under this repo's 400-line
 * limit — no behavior change, same component.
 *
 * Renders the lock preview's four outcomes (lockable, already_held, queued,
 * unresolvable) as distinct, separately-labelled groups, each with a
 * distinct-nośnik count and a collapsible entry list, reusing the shared
 * OutcomeResultBucket/OutcomeResultEntryRow presentation task 4.1 built for
 * the dialog's own post-commit result view.
 *
 * Traceability: offer-accept-reservation / Pre-commit breakdown in the
 * lock-reservations dialog.
 */
import { useTranslation } from 'react-i18next';
import type { BadgeProps } from '@ooh/ui/components/badge';
import type { LockPreviewItem, LockPreviewResponse } from '@ooh/contracts';
import {
  BreakdownGroupChip,
  BreakdownPreviewState,
  distinctUnitCount,
  OutcomeResultBucket,
  OutcomeResultEntryRow,
  outcomeFailedDetail,
  type LooseT,
  type OutcomeResultUnitLookup,
} from '../../reservations/components/outcome-result-buckets';

/** Display order for the lock preview's outcome groups — an outcome with no
 * entries is omitted rather than shown as a zero count (action-breakdown /
 * "Where an outcome applies to no entry, its count SHALL be omitted"). */
const LOCK_BREAKDOWN_OUTCOME_ORDER: LockPreviewItem['outcome'][] = [
  'lockable',
  'already_held',
  'queued',
  'unresolvable',
];

const LOCK_BREAKDOWN_LABEL_KEY: Record<LockPreviewItem['outcome'], string> = {
  lockable: 'workspace.versions.lock.dialog.breakdown.lockableLabel',
  already_held: 'workspace.versions.lock.dialog.breakdown.alreadyHeldLabel',
  queued: 'workspace.versions.lock.dialog.breakdown.queuedLabel',
  unresolvable: 'workspace.versions.lock.dialog.breakdown.unresolvableLabel',
};

// A queued entry is a normal outcome, not an error (action-breakdown / "One
// outcome vocabulary across fan-out actions"); only unresolvable is styled
// destructive.
const LOCK_BREAKDOWN_BADGE_VARIANT: Record<LockPreviewItem['outcome'], BadgeProps['variant']> = {
  lockable: 'success',
  already_held: 'info',
  queued: 'warning',
  unresolvable: 'destructive',
};

interface LockBreakdownGroup {
  outcome: LockPreviewItem['outcome'];
  entries: LockPreviewItem[];
}

function groupLockPreviewItems(items: LockPreviewResponse): LockBreakdownGroup[] {
  return LOCK_BREAKDOWN_OUTCOME_ORDER.map((outcome) => ({
    outcome,
    entries: items.filter((item) => item.outcome === outcome),
  })).filter((group) => group.entries.length > 0);
}

function lockBreakdownEntryDetail(entry: LockPreviewItem, t: LooseT): string | undefined {
  if (entry.outcome === 'queued') {
    return t('workspace.versions.lock.result.entryQueuedPosition', { position: entry.position });
  }
  if (entry.outcome === 'unresolvable') {
    return outcomeFailedDetail(
      {
        errorCode: entry.reason,
        minWindowDays: entry.minWindowDays,
        selectedDays: entry.selectedDays,
      },
      t,
    );
  }
  return undefined;
}

export interface LockBreakdownViewProps {
  preview: {
    isLoading: boolean;
    isError: boolean;
    data: LockPreviewResponse | undefined;
    refetch: () => void;
  };
  lookup: OutcomeResultUnitLookup;
}

export function LockBreakdownView({ preview, lookup }: LockBreakdownViewProps) {
  const { t } = useTranslation('offers');
  const looseT = t as LooseT;
  const unknownUnitLabel = t('workspace.versions.lock.result.unknownUnit');

  const items = preview.data ?? [];
  const groups = groupLockPreviewItems(items);
  // Nothing to lock: no accepted nośnik at all, or every one is already held
  // by this campaign (action-breakdown / "An action with nothing to do says
  // so").
  const nothingToLock = items.every((item) => item.outcome === 'already_held');
  const hasQueued = groups.some((group) => group.outcome === 'queued');

  return (
    <BreakdownPreviewState
      preview={preview}
      testIdPrefix="lock-breakdown"
      loadingLabel={t('workspace.versions.lock.dialog.breakdown.loading')}
      errorLabel={t('workspace.versions.lock.dialog.breakdown.error')}
      retryLabel={t('workspace.versions.lock.dialog.breakdown.retryBtn')}
      isEmpty={nothingToLock}
      emptyLabel={t('workspace.versions.lock.dialog.breakdown.empty')}
    >
      <div className="space-y-2" data-testid="lock-breakdown">
        <div className="flex flex-wrap gap-2" data-testid="lock-breakdown-groups">
          {groups.map((group) => (
            <BreakdownGroupChip
              key={group.outcome}
              testId={`lock-breakdown-group-${group.outcome}`}
              variant={LOCK_BREAKDOWN_BADGE_VARIANT[group.outcome]}
              label={looseT(LOCK_BREAKDOWN_LABEL_KEY[group.outcome])}
              count={distinctUnitCount(group.entries)}
            />
          ))}
        </div>

        {hasQueued ? (
          <p className="text-xs text-muted-foreground" data-testid="lock-breakdown-contended-note">
            {t('workspace.versions.lock.dialog.breakdown.contendedNote')}
          </p>
        ) : null}

        {groups.map((group) => (
          <OutcomeResultBucket<LockPreviewItem>
            key={group.outcome}
            trigger={
              <span className="text-sm font-medium">
                {looseT(LOCK_BREAKDOWN_LABEL_KEY[group.outcome])}
              </span>
            }
            entries={group.entries}
            emptyLabel=""
            testIdPrefix="lock-breakdown-entry"
            renderEntry={(entry) => (
              <OutcomeResultEntryRow
                unit={lookup.get(entry.mediaUnitId)}
                dateFrom={entry.dateFrom}
                dateTo={entry.dateTo}
                detail={lockBreakdownEntryDetail(entry, looseT)}
                unknownUnitLabel={unknownUnitLabel}
              />
            )}
          />
        ))}
      </div>
    </BreakdownPreviewState>
  );
}
